import { MODAL_CONSTANTS } from "@softr-io/blocks-sdk/react";
import { useEffect, useMemo, useSyncExternalStore } from "react";
import { isInModal, isInPreview } from "@softr/utils";
import { OpenPageModal } from "@softr-io/blocks-sdk";
import { patchHistoryWithIndex } from "./historyIndex";

declare global {
    interface Window {
        HISTORY_INDEX_WHEN_MODAL_OPENED?: number;
    }
}

/**
 * To keep track of the history of navigations within the modal,
 * we patch the browser history api to keep track of how many entries have been pushed since the modal
 * was opened. This allows us to ensure that the close button pops off enough entries to go back to the
 * state when the modal was opened. Simply calling pushState when the close button is clicked would
 * mean that if the user used the browser back button after closing the modal, they would end up re-opening it.
 */
export function useModalNavHistory() {
    useEffect(() => {
        if (isInModal()) return;
        const restore = patchHistoryWithIndex();
        return restore;
    }, []);
}

export function setOpenPageModal(modal: OpenPageModal | null) {
    const targetWindow = isInModal() ? window.parent : window;
    const url = new URL(targetWindow.location.href);
    setModalSearchParams(url, modal);

    let type: "FIRST_OPEN" | "CLOSE_MODAL" | undefined;

    if (!isInModal() && modal && !getModalSrcSnapshot()) {
        type = "FIRST_OPEN";
        targetWindow.HISTORY_INDEX_WHEN_MODAL_OPENED = targetWindow.historyIndex.current;
    }
    if (!isInModal() && !modal && getModalSrcSnapshot()) {
        type = "CLOSE_MODAL";
    }

    switch (type) {
        case "CLOSE_MODAL": {
            // When closing the modal by pressing a button, we want to go back in history to the
            // point where the modal was opened.
            // If we just did a history.pushState here, pressing the browser back button after
            // closing the modal would reopen it, which is confusing.
            // This hack ensures that the navigations within the modal traversable with the back/forward
            // buttons, while still keeping the URL in sync with the modal state.
            if (targetWindow.HISTORY_INDEX_WHEN_MODAL_OPENED !== undefined) {
                targetWindow.historyIndex.restore(targetWindow.HISTORY_INDEX_WHEN_MODAL_OPENED);
            } else {
                // If the user opened the page with a modal already open, we won't have a saved history index.
                // The best we can there is just replace the current history to erase the modal params
                targetWindow.history.replaceState(null, "", url.toString());
            }
            break;
        }
        default:
            targetWindow.history.pushState(null, "", url.toString());
            break;
    }
    targetWindow.dispatchEvent(new CustomEvent("@softr/page-modal-change"));

    if (isInPreview()) {
        // Totally optional, but we always try to keep the preview window's URL in sync with
        // the actual application iframe.
        syncModalWithPreviewWindow(modal);
    }
    if (modal) {
        // If a v4 block's modal was already open, we don't want both modals to be open at the same time
        closeLegacyModals();
    }
}
/**
 * Modal depth is the number of forward navigations that have been done since the modal was opened.
 * Used to control whether we show a "Back" button on the modal header
 */
export function useModalDepth(): number {
    return useSyncExternalStore(subscribeToModalDepthChanges, getModalDepthSnapshot);
}

function subscribeToModalDepthChanges(onChange: () => void) {
    const targetWindow = isInModal() ? window.parent : window;
    targetWindow.addEventListener("@softr/page-modal-change", onChange);
    targetWindow.addEventListener("popstate", onChange);
    const cleanupNavigationListener = targetWindow.SoftrPageRenderer.addNavigationListener(onChange);
    return () => {
        targetWindow.removeEventListener("@softr/page-modal-change", onChange);
        targetWindow.removeEventListener("popstate", onChange);
        cleanupNavigationListener();
    };
}

function getModalDepthSnapshot(): number {
    const targetWindow = isInModal() ? window.parent : window;
    return targetWindow.historyIndex.current - (targetWindow.HISTORY_INDEX_WHEN_MODAL_OPENED ?? 0);
}

export function useOpenPageModal(): OpenPageModal | null {
    const src = useSyncExternalStore(subscribeToModalChanges, getModalSrcSnapshot);
    const size = useSyncExternalStore(subscribeToModalChanges, getModalSizeSnapshot);
    const placement = useSyncExternalStore(subscribeToModalChanges, getModalPlacementSnapshot);

    return useMemo(() => (src && size && placement ? { src, size, placement } : null), [src, size, placement]);
}

export function setModalSearchParams(url: URL, modal: OpenPageModal | null) {
    if (modal === null) {
        url.searchParams.delete(MODAL_CONSTANTS.PARAM_SRC);
        url.searchParams.delete(MODAL_CONSTANTS.PARAM_SIZE);
        url.searchParams.delete(MODAL_CONSTANTS.PARAM_PLACEMENT);
    } else {
        if (modal.src.startsWith("data:")) {
            localStorage.setItem("modalDataSrc", modal.src);
            url.searchParams.set(MODAL_CONSTANTS.PARAM_SRC, "data:");
        } else {
            url.searchParams.set(MODAL_CONSTANTS.PARAM_SRC, relativizeIfSameOrigin(modal.src));
        }
        url.searchParams.set(MODAL_CONSTANTS.PARAM_SIZE, modal.size);
        url.searchParams.set(MODAL_CONSTANTS.PARAM_PLACEMENT, modal.placement);

        try {
            // These 2 are optional, but it's nice to try to keep the legacy modal's size and placement in sync with the
            // new dialog's URL params so that the modal size doesn't shift when clicking a v5 block's "Open in modal"
            // link from within a v4 block's modal
            setModalSizeIfLegacyModalOpen(url);
            setModalPlacementIfLegacyModalOpen(url);
        } catch (error) {
            console.error(
                "Error while trying to set modal size and placement based on legacy modal state; Falling back to the action config",
                error,
            );
        }
    }
}

const microModalSizeMap = {
    sm: "S",
    md: "M",
    lg: "L",
    xl: "FULL",
} as const;

function syncModalWithPreviewWindow(modal: OpenPageModal | null) {
    const targetWindow = window.top ?? window.parent;
    const url = new URL(targetWindow.location.href);
    setModalSearchParams(url, modal);
    targetWindow.history.replaceState(null, "", url.toString());
}

function getModalSrcSnapshot(): OpenPageModal["src"] | null {
    const targetWindow = isInModal() ? window.parent : window;
    const url = new URLSearchParams(targetWindow.location.search).get(MODAL_CONSTANTS.PARAM_SRC) ?? null;
    if (!url) {
        return null;
    }
    if (url === "data:") {
        return localStorage.getItem("modalDataSrc");
    } else {
        return url;
    }
}
const VALID_DIALOG_SIZES = {
    S: true,
    M: true,
    L: true,
    FULL: true,
} as const;
const VALID_DIALOG_PLACEMENTS = {
    center: true,
    start: true,
    end: true,
} as const;
function asDialogSize(size: string): OpenPageModal["size"] {
    if (size in VALID_DIALOG_SIZES) {
        return size as OpenPageModal["size"];
    } else {
        return "M";
    }
}
function asDialogPlacement(placement: string): OpenPageModal["placement"] {
    if (placement in VALID_DIALOG_PLACEMENTS) {
        return placement as OpenPageModal["placement"];
    } else {
        return "center";
    }
}
function getModalSizeSnapshot(): OpenPageModal["size"] {
    const targetWindow = isInModal() ? window.parent : window;
    const size = new URLSearchParams(targetWindow.location.search).get(MODAL_CONSTANTS.PARAM_SIZE) ?? "M";
    return asDialogSize(size);
}

function getModalPlacementSnapshot(): OpenPageModal["placement"] {
    const targetWindow = isInModal() ? window.parent : window;
    const placement =
        new URLSearchParams(targetWindow.location.search).get(MODAL_CONSTANTS.PARAM_PLACEMENT) ?? "center";
    return asDialogPlacement(placement);
}

function subscribeToModalChanges(onChange: () => void) {
    const targetWindow = isInModal() ? window.parent : window;
    targetWindow.addEventListener("@softr/page-modal-change", onChange);
    targetWindow.addEventListener("popstate", onChange);
    const cleanupNavigationListener = targetWindow.SoftrPageRenderer.addNavigationListener(onChange);
    return () => {
        targetWindow.removeEventListener("@softr/page-modal-change", onChange);
        targetWindow.removeEventListener("popstate", onChange);
        cleanupNavigationListener();
    };
}

function relativizeIfSameOrigin(url: string) {
    if (url.startsWith(window.location.origin)) {
        return url.slice(window.location.origin.length);
    } else {
        return url;
    }
}

function setModalSizeIfLegacyModalOpen(url: URL) {
    const targetWindow = isInModal() ? window.parent : window;
    const classList = targetWindow.document.getElementById("sw-modal")?.querySelector(".sw-modal-container")?.classList;
    if (!classList) return;

    const modalSizeClass = [...classList].find((it) => it.startsWith("sw-modal-size-"));
    const modalSize = modalSizeClass?.split("sw-modal-size-")[1];
    if (!modalSize) return;
    const dialogSize = microModalSizeMap[modalSize as keyof typeof microModalSizeMap] ?? "M";
    url.searchParams.set(MODAL_CONSTANTS.PARAM_SIZE, dialogSize);
}
function setModalPlacementIfLegacyModalOpen(url: URL) {
    const targetWindow = isInModal() ? window.parent : window;
    const v4SlidingModal = targetWindow.document.querySelector("[data-testid='sliding-modal']") as HTMLElement | null;
    if (v4SlidingModal) {
        url.searchParams.set(MODAL_CONSTANTS.PARAM_PLACEMENT, "end");
    } else if (targetWindow.document.querySelector("#sw-modal.is-open")) {
        url.searchParams.set(MODAL_CONSTANTS.PARAM_PLACEMENT, "center");
    }
}

function closeLegacyModals() {
    const targetWindow = isInModal() ? window.parent : window;

    const v4SlidingModalCloseButton = targetWindow.document.querySelector(
        "[data-testid='sliding-modal'] .MuiModal-backdrop",
    ) as HTMLElement | null;
    if (v4SlidingModalCloseButton) {
        v4SlidingModalCloseButton.click();
    }

    if (targetWindow.document.querySelector("#sw-modal.is-open")) {
        targetWindow.MicroModal?.close("sw-modal");
    }
}

declare global {
    interface Window {
        MicroModal?: {
            close: (id: string) => void;
        };
    }
}
